<template>
<AppLayout>
  <div class="procurement-report">
    <div class="container">
       <div class="filter-search-header mt-3">
        <h6 class="fw-bold">Procurement</h6>
      </div>
      <div class="filter-search" style="margin-top: 0px;">
        <div class="filter-search-body">
          <div class="form">
            <div class="row">
              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <label for="dateFrom">Date From</label>
                  <input type="date" class="form-control" v-model="filters.startDate">
                </div>
              </div>

              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <label for="dateTo">Date To</label>
                  <input type="date" class="form-control" v-model="filters.endDate">
                </div>
              </div>

              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <label for="interval">Interval</label>
                  <select class="form-control" v-model="filters.interval">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
              </div>

              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <label for="productId">Product</label>
                  <Select2 v-model="filters.productId" :options="productOptions" :settings="{ width: '100%' }" />
                </div>
              </div>

              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <label for="serviceId">Service</label>
                  <Select2 v-model="filters.serviceId" :options="serviceOptions" :settings="{ width: '100%' }" />
                </div>
              </div>

              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <label class="d-block">&nbsp;</label>
                  <button class="btn btn-sm subtle-btn w-100" style="height: 32px; font-size: 11px; display: flex; align-items: center; justify-content: center;" @click="clearFilters">Clear Filters</button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <hr>

      <div v-if="loading" class="mt-4">
        <table class="table">
          <thead>
            <tr>
              <th><div class="skeleton-box" style="width: 150px; height: 16px;"></div></th>
              <th><div class="skeleton-box" style="width: 100px; height: 16px;"></div></th>
              <th><div class="skeleton-box" style="width: 120px; height: 16px;"></div></th>
              <th><div class="skeleton-box" style="width: 120px; height: 16px;"></div></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="i in 5" :key="i">
              <td><div class="skeleton-box" style="width: 80%; height: 14px;"></div></td>
              <td><div class="skeleton-box" style="width: 40%; height: 14px;"></div></td>
              <td><div class="skeleton-box" style="width: 60%; height: 14px;"></div></td>
              <td><div class="skeleton-box" style="width: 60%; height: 14px;"></div></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!loading && flatItems.length > 0" class="mt-4">
        <div class="d-flex justify-content-end mb-3" style="gap: 10px;">
          <button class="btn btn-sm subtle-btn" @click="downloadCSV">
            <i class="fas fa-file-csv text-secondary"></i> Download CSV
          </button>
          <button class="btn btn-sm subtle-btn" @click="downloadPDF">
            <i class="fas fa-file-pdf text-danger"></i> Download PDF
          </button>
        </div>
        <div ref="reportTable">
          <table class="table table-hover">
          <thead>
            <tr>
              <th @click="sortBy('name')" class="cursor-pointer">
                Product Name
                <i v-if="sortKey === 'name'" :class="sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                <i v-else class="fas fa-sort text-muted" style="opacity: 0.3;"></i>
              </th>
              <th @click="sortBy('quantity')" class="cursor-pointer">
                Quantity
                <i v-if="sortKey === 'quantity'" :class="sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                <i v-else class="fas fa-sort text-muted" style="opacity: 0.3;"></i>
              </th>
              <th @click="sortBy('averagePrice')" class="cursor-pointer">
                Average Price
                <i v-if="sortKey === 'averagePrice'" :class="sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                <i v-else class="fas fa-sort text-muted" style="opacity: 0.3;"></i>
              </th>
              <th>Trend</th>
              <th @click="sortBy('cost')" class="cursor-pointer">
                Amount
                <i v-if="sortKey === 'cost'" :class="sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                <i v-else class="fas fa-sort text-muted" style="opacity: 0.3;"></i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedData" :key="item.name">
              <td class="align-middle">{{ item.name }}</td>
              <td class="align-middle">{{ item.quantity }}</td>
              <td class="align-middle">{{ formatCurrency(item.averagePrice) }}</td>
              <td class="align-middle" :title="getTrendTooltip(item.trendDetails)">
                <svg v-if="item.trendPrices && item.trendPrices.length > 1" width="100" height="30" viewBox="0 0 100 30" style="cursor: help;">
                  <polyline class="draw-animation" pathLength="100" :points="getPoints(item.trendPrices)" fill="none" :stroke="getTrendColor(item.trendPrices)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span v-else class="text-muted" style="font-size: 11px;">Not enough data</span>
              </td>
              <td class="align-middle font-weight-bold">{{ formatCurrency(item.cost) }}</td>
            </tr>
          </tbody>
        </table>
        </div>
        
        <!-- Pagination -->
        <div v-if="totalPages > 1" class="d-flex justify-content-end align-items-center mt-3" style="padding-top: 10px; gap: 15px;">
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="btn btn-outline btn-sm"
          >
            <i class="fas fa-chevron-left"></i> Previous
          </button>
          <span class="text-muted" style="font-size: 0.9em;">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="btn btn-outline btn-sm"
          >
            Next <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      
      <div v-if="!loading && flatItems.length === 0 && hasSearched" class="alert alert-info mt-4">
        No procurement costs found for the given criteria.
      </div>

    </div>
  </div>
</AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import Select2 from 'vue3-select2-component';
import exportFromJSON from 'export-from-json';
import html2pdf from 'html2pdf.js';

export default {
  name: 'ProcurementReport',
  components: {
    AppLayout,
    Select2
  },
  data() {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    
    // Format to YYYY-MM-DD
    const formatDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    let token = null;
    try {
      token = JSON.parse(localStorage.getItem('access_token'));
    } catch(e) {
      token = localStorage.getItem('access_token');
    }

    return {
      token: token,
      loading: false,
      hasSearched: false,
      filters: {
        startDate: formatDate(startOfYear),
        endDate: formatDate(today),
        interval: 'monthly',
        productId: '',
        serviceId: ''
      },
      reportData: [],
      products: [],
      services: [],
      currentPage: 1,
      itemsPerPage: 20,
      sortKey: 'name',
      sortOrder: 'asc'
    }
  },
  computed: {
    productOptions() {
      return [{ id: '', text: 'All Products' }, ...this.products.map(p => ({ id: p.id, text: p.name }))];
    },
    serviceOptions() {
      return [{ id: '', text: 'All Services' }, ...this.services.map(s => ({ id: s.id, text: s.name }))];
    },
    flatItems() {
      const itemsMap = {};
      
      // Initialize items map across all periods to track prices properly
      this.reportData.forEach(row => {
        if (row.items) {
          row.items.forEach(item => {
            if (!itemsMap[item.name]) {
              itemsMap[item.name] = { 
                name: item.name, 
                quantity: 0, 
                cost: 0, 
                averagePrice: 0, 
                unit: item.unit || 'unit',
                prices: new Array(this.reportData.length).fill(null)
              };
            }
          });
        }
      });
      
      // Populate data
      this.reportData.forEach((row, index) => {
        if (row.items) {
          row.items.forEach(item => {
            itemsMap[item.name].quantity += item.quantity;
            itemsMap[item.name].cost += item.cost;
            itemsMap[item.name].prices[index] = item.quantity > 0 ? item.cost / item.quantity : null;
          });
        }
      });
      
      const filteredItems = Object.values(itemsMap).filter(item => item.cost > 0);
      filteredItems.forEach(item => {
        item.averagePrice = item.quantity > 0 ? item.cost / item.quantity : 0;
        item.trendPrices = item.prices.filter(p => p !== null);
        item.trendDetails = [];
        this.reportData.forEach((row, index) => {
          if (item.prices[index] !== null) {
            item.trendDetails.push({ period: row.period, price: item.prices[index] });
          }
        });
      });
      return filteredItems;
    },
    sortedItems() {
      let items = [...this.flatItems];
      if (this.sortKey) {
        items = items.sort((a, b) => {
          let modifier = this.sortOrder === 'desc' ? -1 : 1;
          if (a[this.sortKey] < b[this.sortKey]) return -1 * modifier;
          if (a[this.sortKey] > b[this.sortKey]) return 1 * modifier;
          return 0;
        });
      }
      return items;
    },
    totalPages() {
      return Math.ceil(this.sortedItems.length / this.itemsPerPage) || 1;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.sortedItems.slice(start, start + this.itemsPerPage);
    }
  },
  watch: {
    filters: {
      handler() {
        this.fetchReport();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchProducts();
    this.fetchServices();
    this.fetchReport();
  },
  methods: {
    clearFilters() {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const formatDate = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      this.filters = {
        startDate: formatDate(startOfYear),
        endDate: formatDate(today),
        interval: 'monthly',
        productId: '',
        serviceId: ''
      };
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    async fetchProducts() {
      try {
        const requestOptions = {
          method: "GET",
          headers: { 
            "Content-Type": "application/json", 
            'Authorization': 'Bearer ' + this.token 
          }
        };
        const res = await fetch(`${process.env.VUE_APP_URL}/products`, requestOptions);
        if (res.ok) {
          const data = await res.json();
          this.products = data.products || data.data || data;
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },
    async fetchServices() {
      try {
        const requestOptions = {
          method: "GET",
          headers: { 
            "Content-Type": "application/json", 
            'Authorization': 'Bearer ' + this.token 
          }
        };
        const res = await fetch(`${process.env.VUE_APP_URL}/services`, requestOptions);
        if (res.ok) {
          const data = await res.json();
          this.services = data.services || data.data || data;
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    },
    async fetchReport() {
      this.loading = true;
      this.hasSearched = true;
      this.reportData = [];
      this.currentPage = 1;

      try {
        const queryParams = new URLSearchParams();
        if (this.filters.startDate) queryParams.append('startDate', this.filters.startDate);
        if (this.filters.endDate) queryParams.append('endDate', this.filters.endDate);
        if (this.filters.interval) queryParams.append('interval', this.filters.interval);
        if (this.filters.productId) queryParams.append('productId', this.filters.productId);
        if (this.filters.serviceId) queryParams.append('serviceId', this.filters.serviceId);

        const requestOptions = {
          method: "GET",
          headers: { 
            "Content-Type": "application/json", 
            'Authorization': 'Bearer ' + this.token 
          }
        };

        const res = await fetch(`${process.env.VUE_APP_URL}/reports/procurement-costs?${queryParams.toString()}`, requestOptions);
        
        if (res.ok) {
          const data = await res.json();
          this.reportData = data.data || data; 
        } else {
          console.error("Failed to fetch report:", await res.text());
        }
      } catch (error) {
        console.error("Error fetching procurement report:", error);
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      if (value == null) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(value);
    },
    getTrendTooltip(details) {
      if (!details || details.length === 0) return '';
      return 'Price History:\n' + details.map(d => `${d.period}: ${this.formatCurrency(d.price)}`).join('\n');
    },
    getPoints(prices) {
      if (!prices || prices.length < 2) return '';
      const max = Math.max(...prices);
      const min = Math.min(...prices);
      const range = max - min || 1; // avoid division by zero
      
      const width = 100;
      const height = 24; 
      
      return prices.map((price, index) => {
        const x = (index / (prices.length - 1)) * width;
        const y = 30 - 3 - ((price - min) / range) * height; 
        return `${x},${y}`;
      }).join(' ');
    },
    getTrendColor(prices) {
      if (!prices || prices.length < 2) return '#ccc';
      const first = prices[0];
      const last = prices[prices.length - 1];
      if (last > first) return '#ff6b6b'; // red (cost went up)
      if (last < first) return '#4caf50'; // green (cost went down)
      return '#2196f3'; // blue (stable)
    },
    downloadCSV() {
      const data = this.sortedItems.map(item => ({
        'Product Name': item.name,
        'Quantity': item.quantity,
        'Average Price': item.averagePrice,
        'Amount': item.cost
      }));
      
      const fileName = `Procurement_Report_${this.filters.startDate}_to_${this.filters.endDate}`;
      const exportType = exportFromJSON.types.csv;
      exportFromJSON({ data, fileName, exportType });
    },
    async downloadPDF() {
      const originalItemsPerPage = this.itemsPerPage;
      this.itemsPerPage = 999999;
      this.currentPage = 1;
      
      await this.$nextTick();
      
      const element = this.$refs.reportTable;
      const opt = {
        margin:       0.5,
        filename:     `Procurement_Report_${this.filters.startDate}_to_${this.filters.endDate}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2 },
        jsPDF:        { unit: 'in', format: 'letter', orientation: 'landscape' }
      };
      
      html2pdf().from(element).set(opt).save().then(() => {
        this.itemsPerPage = originalItemsPerPage;
      });
    }
  }
}
</script>

<style scoped>
.filter-search {
  background: #fff;
  padding: 10px;
  border-radius: 8px;
}

.table {
  margin: 0;
  table-layout: fixed;
}

:deep(.select2-container .select2-selection--single) {
  height: 32px;
  border: 1px solid #cedbf3 !important;
  border-radius: 3px;
}
:deep(.select2-container .select2-selection--single .select2-selection__rendered) {
  line-height: 32px;
  padding-left: 10px;
  font-size: 11px;
}
:deep(.select2-container .select2-selection--single .select2-selection__arrow) {
  height: 32px;
}

/* Skeleton Loader Styles */
.skeleton-box {
  display: inline-block;
  position: relative;
  overflow: hidden;
  background-color: #e9ecef;
  border-radius: 4px;
}

.skeleton-box::after {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0,
    rgba(255, 255, 255, 0.2) 20%,
    rgba(255, 255, 255, 0.5) 60%,
    rgba(255, 255, 255, 0)
  );
  animation: shimmer 1.5s infinite;
  content: '';
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

/* Sparkline drawing animation */
.draw-animation {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: drawLine 1.5s ease-in-out forwards;
}

@keyframes drawLine {
  to {
    stroke-dashoffset: 0;
  }
}

/* Subtle border for buttons */
.subtle-btn {
  background-color: #fff;
  border: 1px solid #e0e4e8;
  color: #495057;
  font-weight: 500;
  transition: all 0.2s ease;
}
.subtle-btn:hover {
  background-color: #f8f9fa;
  border-color: #ced4da;
}
</style>
